// Mirrors src/shared/bracket.ts's nextPowerOfTwo/seedingOrder (backend, pure
// function, no AWS imports) — REPLICATED here rather than imported, for the
// SAME reason groupPlan.ts already documents: the frontend is a fully
// separate TypeScript/Vite project with no workspace/path-alias wiring back
// to the backend's src/shared. Kept intentionally tiny + copied verbatim so
// it's trivial to eyeball against bracket.ts if that ever changes.
//
// Used ONLY for the elimination-gate proposal's live "round 1 pairings"
// preview (StagePipelinePanel.tsx's EliminationProposalPanel) — a read-only
// client-side projection of what generateBracket() would produce for the
// CURRENT draft order, computed as the admin reorders seeds, before Confirm
// ever calls the server. This does not replicate the bracket engine itself
// (byes/losers-bracket/advancement) — just enough to preview round 1.

/** Next power of two ≥ n. */
export function nextPowerOfTwo(n: number): number {
  let p = 1;
  while (p < n) p *= 2;
  return p;
}

/**
 * Standard bracket seed placement for a field of `size` (power of two):
 * position i of round 1 is filled by seed seedingOrder(size)[i]. e.g. size 8
 * -> [1,8,4,5,2,7,3,6].
 */
export function seedingOrder(size: number): number[] {
  let order = [1];
  while (order.length < size) {
    const m = order.length * 2;
    const next: number[] = [];
    for (const s of order) next.push(s, m + 1 - s);
    order = next;
  }
  return order;
}

export interface Round1Pairing {
  seedA: number;
  seedB: number;
  teamA: string | null; // null = a bye (field isn't a full power of two)
  teamB: string | null;
}

/**
 * Round-1 pairings implied by `entrants` (position = seed, index 0 = seed 1)
 * — the SAME preview generateBracket() would produce for round 1 (byes fill
 * seed slots beyond entrants.length; who a bye-holder ultimately meets in
 * round 2 is out of scope for this preview).
 */
export function previewRound1(entrants: string[]): Round1Pairing[] {
  const n = entrants.length;
  if (n < 2) return [];
  const size = nextPowerOfTwo(n);
  const order = seedingOrder(size);
  const nameForSeed = (seed: number): string | null => (seed <= n ? entrants[seed - 1] : null);
  const pairings: Round1Pairing[] = [];
  for (let i = 0; i < size / 2; i++) {
    const seedA = order[2 * i];
    const seedB = order[2 * i + 1];
    pairings.push({ seedA, seedB, teamA: nameForSeed(seedA), teamB: nameForSeed(seedB) });
  }
  return pairings;
}
